import React from "react";
import { Card, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import type {
    BadgeZoneViolationIncident,
    ShockEvent,
    VehicleZoneViolationIncident,
} from "../../../api";
import { store } from "../../../store";
import { Skeleton } from "./skeleton";
import { useTranslation } from "react-i18next";
import { Distance } from "@/components/ui/distance";

export type EventMetaCardProps = React.PropsWithChildren &
    (
        | {
              badgeZoneViolationIncident?: BadgeZoneViolationIncident;
              shockEvent?: never;
              vehicleZoneViolationIncident?: never;
          }
        | {
              badgeZoneViolationIncident?: never;
              shockEvent?: ShockEvent;
              vehicleZoneViolationIncident?: never;
          }
        | {
              badgeZoneViolationIncident?: never;
              shockEvent?: never;
              vehicleZoneViolationIncident?: VehicleZoneViolationIncident;
          }
    ) & { loading?: boolean };

export const EventMetaCard = (props: EventMetaCardProps): JSX.Element => {
    const { t } = useTranslation();
    const minimumDistance =
        props.badgeZoneViolationIncident?.minimumDistanceCm ??
        props.vehicleZoneViolationIncident?.minimumDistanceCm;

    const incidentTime =
        (props.badgeZoneViolationIncident ?? props.vehicleZoneViolationIncident)?.startTime ??
        props.shockEvent?.time;

    const startTime = (props.badgeZoneViolationIncident ?? props.vehicleZoneViolationIncident)
        ?.startTime;
    const stopTime = (props.badgeZoneViolationIncident ?? props.vehicleZoneViolationIncident)
        ?.stopTime;

    return (
        <Card>
            <CardHeader className="flex flex-row justify-between space-y-0">
                <div className="flex flex-col gap-2">
                    <div className="text-muted-foreground font-medium">
                        {t("component.eventMetaCard.incidentTime")}
                    </div>
                    {props.loading ? (
                        <Skeleton className="h-4 w-40" />
                    ) : (
                        <div className="font-semibold">
                            {incidentTime
                                ? store.i18n.formatDateTime(incidentTime)
                                : t("component.eventMetaCard.unknown")}
                        </div>
                    )}
                </div>

                {startTime && stopTime && (
                    <>
                        <Separator orientation="vertical" />
                        <div className="flex flex-col gap-2">
                            <div className="text-muted-foreground font-medium">
                                {t("component.eventMetaCard.incidentDuration")}
                            </div>
                            <div className="font-semibold">
                                {props.loading && <Skeleton className="h-4 w-24" />}
                                {!props.loading &&
                                    store.i18n.formatDuration(
                                        (stopTime.getTime() - startTime.getTime()) / 1000,
                                        true,
                                        false,
                                    )}
                            </div>
                        </div>
                    </>
                )}

                <Separator orientation="vertical" />
                {("badgeZoneViolationIncident" in props ||
                    "vehicleZoneViolationIncident" in props) && (
                    <>
                        <div className="flex flex-col gap-2">
                            <div className="text-muted-foreground font-medium">
                                {t("component.eventMetaCard.minimumDistance")}
                            </div>
                            <div className="font-semibold">
                                {props.loading && <Skeleton className="h-4 w-24" />}
                                {!props.loading && minimumDistance != null && (
                                    <Distance cm={minimumDistance} />
                                )}
                            </div>
                        </div>
                        <Separator orientation="vertical" />
                        <div className="flex flex-col gap-2">
                            <div className="text-muted-foreground font-medium">
                                {t("component.eventMetaCard.triggeredZone")}
                            </div>
                            <div className="font-semibold">
                                {props.loading ? (
                                    <Skeleton className="h-4 w-24" />
                                ) : (
                                    t(
                                        `schema.proximityZone.${
                                            props.badgeZoneViolationIncident?.triggeredZone ??
                                            props.vehicleZoneViolationIncident?.triggeredZone
                                        }`,
                                    )
                                )}
                            </div>
                        </div>
                    </>
                )}

                {"shockEvent" in props && (
                    <div className="flex flex-col gap-2">
                        <div className="text-muted-foreground font-medium">
                            {t("component.eventMetaCard.impactLevel")}
                        </div>
                        <div className="font-semibold">
                            {props.loading ? (
                                <Skeleton className="h-4 w-12" />
                            ) : (
                                t(`schema.shockEvent.shockLevel.${props.shockEvent?.shockLevel}`)
                            )}
                        </div>
                    </div>
                )}
            </CardHeader>
        </Card>
    );
};
